/**
 * Typed wrapper for the G11 tenant announcements feed + HR-admin CRUD.
 *
 *   GET    /api/v1/announcements        (any authed tenant user)
 *   POST   /api/v1/announcements        (HR-admin: hrRole + CSRF + audit)
 *   PATCH  /api/v1/announcements/{id}    (HR-admin: hrRole + CSRF + audit)
 *   DELETE /api/v1/announcements/{id}    (HR-admin: hrRole + CSRF + audit)
 *
 * Any authenticated tenant user reaches the list endpoint; the write
 * endpoints are HR-admin gated at the router (the dashboard surface
 * gates the same set via `isHRAdmin` for UX, the backend is the source
 * of truth). Auth + CSRF are inherited from the shared axios client
 * (lib/api/client.ts); we never send a client-supplied tenant_id —
 * the server derives it from the session for every route.
 */

import { api } from "./client";

export const ANNOUNCEMENTS_BASE = "/api/v1/announcements";

export interface Announcement {
  id: number;
  title: string;
  body: string;
  posted_by_id: number;
  posted_at: string;
  is_pinned: boolean;
  /** Nullable on the wire when the announcement does not auto-expire. */
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListAnnouncementsResponse {
  items: Announcement[];
}

export async function listAnnouncements(
  signal?: AbortSignal,
): Promise<ListAnnouncementsResponse> {
  const { data } = await api.get<ListAnnouncementsResponse>(
    ANNOUNCEMENTS_BASE,
    { signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// HR-admin writes — request shapes mirror backend/internal/handlers/announcements.go
// (createAnnouncementRequest / updateAnnouncementRequest).
// ---------------------------------------------------------------------------

export interface CreateAnnouncementRequest {
  title: string;
  body: string;
  is_pinned?: boolean;
  /**
   * RFC3339 timestamp, or omit / null for "never expires". The backend
   * treats a missing value as no-expiry.
   */
  expires_at?: string | null;
}

export interface UpdateAnnouncementRequest {
  title?: string;
  body?: string;
  is_pinned?: boolean;
  /**
   * RFC3339 timestamp to (re)set an expiry; "" clears it (never
   * expires). Omit the field entirely to leave the current expiry
   * untouched (PATCH semantics).
   */
  expires_at?: string;
}

export async function createAnnouncement(
  body: CreateAnnouncementRequest,
): Promise<Announcement> {
  const { data } = await api.post<Announcement>(ANNOUNCEMENTS_BASE, body);
  return data;
}

export async function updateAnnouncement(
  id: number,
  body: UpdateAnnouncementRequest,
): Promise<Announcement> {
  const { data } = await api.patch<Announcement>(
    `${ANNOUNCEMENTS_BASE}/${id}`,
    body,
  );
  return data;
}

export async function deleteAnnouncement(id: number): Promise<void> {
  await api.delete(`${ANNOUNCEMENTS_BASE}/${id}`);
}
